import { useMemo, useRef, useState } from 'react';
import { PageHeader, Card, Input, TextArea, Button } from 'components/ui';
import { ProductGrid } from 'components/ProductGrid';
import { useToast } from 'components/toast/ToastProvider';
import { formatCurrency } from 'lib/formatters';
import { generateClientRequestId } from 'api/graphql/inventory';
import { useVendorCatalog } from 'features/catalog/hooks/useVendorCatalog';
import { useRecordInstoreSale } from '../hooks/useRecordInstoreSale';
import { SaleCart } from '../components/SaleCart';

/** Point-of-sale style screen: search/grid → cart → record sale. */
export function RecordSalePage() {
  const { items: catalog, loading: catalogLoading, error, refetch } = useVendorCatalog();
  const { recordSale, loading: saving } = useRecordInstoreSale();
  const toast = useToast();

  const [cart, setCart] = useState([]);
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');

  // Stable idempotency key for the current sale; regenerated after success so a
  // failed submit can be safely retried without double-counting.
  const requestIdRef = useRef(generateClientRequestId());

  // Stock available for an item (Infinity when unknown).
  const stockOf = (item) => (item?.stockQuantity != null ? Number(item.stockQuantity) : Infinity);

  const addItem = (item) => {
    const max = stockOf(item);
    if (max <= 0) return; // out of stock — also disabled in the grid
    const existing = cart.find((line) => line.item.vendorCatalogId === item.vendorCatalogId);
    if (existing && existing.quantity >= max) {
      toast.info('Stock limit reached', `Only ${max} in stock for this product.`);
      return;
    }
    setCart((current) => {
      const line = current.find((entry) => entry.item.vendorCatalogId === item.vendorCatalogId);
      if (line) {
        return current.map((entry) =>
          entry.item.vendorCatalogId === item.vendorCatalogId
            ? { ...entry, quantity: Math.min(entry.quantity + 1, max) }
            : entry,
        );
      }
      return [...current, { item, quantity: 1 }];
    });
  };

  const changeQuantity = (vendorCatalogId, quantity) =>
    setCart((current) => {
      const line = current.find((entry) => entry.item.vendorCatalogId === vendorCatalogId);
      const capped = Math.min(quantity, stockOf(line?.item));
      return capped <= 0
        ? current.filter((entry) => entry.item.vendorCatalogId !== vendorCatalogId)
        : current.map((entry) =>
            entry.item.vendorCatalogId === vendorCatalogId ? { ...entry, quantity: capped } : entry,
          );
    });

  const removeItem = (vendorCatalogId) =>
    setCart((current) => current.filter((line) => line.item.vendorCatalogId !== vendorCatalogId));

  const cartQuantity = (vendorCatalogId) =>
    cart.find((line) => line.item.vendorCatalogId === vendorCatalogId)?.quantity || 0;

  const total = useMemo(
    () =>
      cart.reduce((sum, line) => sum + (Number(line.item.vendorPrice?.amount) || 0) * line.quantity, 0),
    [cart],
  );

  const handleSubmit = async () => {
    if (cart.length === 0) return;
    try {
      const payload = await recordSale({
        items: cart.map((line) => ({
          vendorCatalogId: line.item.vendorCatalogId,
          quantity: line.quantity,
        })),
        customerPhone: customerPhone.trim() || undefined,
        notes: notes.trim() || undefined,
        clientRequestId: requestIdRef.current,
      });

      if (payload?.success) {
        toast.success('Sale recorded', payload.message || 'Stock has been updated.');
        setCart([]);
        setCustomerPhone('');
        setNotes('');
        requestIdRef.current = generateClientRequestId();
      } else {
        toast.error(
          'Could not record sale',
          payload?.errors?.[0]?.message || payload?.message || 'Please try again.',
        );
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Record in-store sale"
        description="Tap products to build the sale — stock updates automatically."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ProductGrid
            items={catalog}
            loading={catalogLoading}
            error={error}
            onRetry={refetch}
            onSelect={addItem}
            getBadge={(item) => cartQuantity(item.vendorCatalogId)}
            isItemDisabled={(item) => item.stockQuantity != null && Number(item.stockQuantity) <= 0}
          />
        </div>

        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <SaleCart cart={cart} onQuantityChange={changeQuantity} onRemove={removeItem} />

          <Card>
            <div className="space-y-4">
              <Input
                label="Customer phone"
                leftAddon="+91"
                placeholder="Optional"
                type="tel"
                inputMode="numeric"
                value={customerPhone}
                onChange={(event) => setCustomerPhone(event.target.value)}
              />
              <TextArea
                label="Notes"
                rows={2}
                placeholder="Optional"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
              <div className="flex items-center justify-between border-t border-surface-200 pt-4">
                <span className="text-sm text-slate-500">Total</span>
                <span className="text-lg font-semibold text-slate-900">{formatCurrency(total)}</span>
              </div>
              <Button
                fullWidth
                size="lg"
                disabled={cart.length === 0}
                isLoading={saving}
                onClick={handleSubmit}
              >
                Record sale
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default RecordSalePage;
